import PropTypes from "prop-types";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import activityService from "../services/activityService";

const ActivityContext = createContext();

export function ActivityProvider({ children }) {
  const [activities, setActivities] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1,
    page_size: 20,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchActivities = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await activityService.listActivities(filters);
      const results = response.results || [];
      setActivities(results);
      setPagination({
        count: response.count || results.length,
        next: response.next || null,
        previous: response.previous || null,
        page: Number(filters.page || 1),
        page_size: Number(filters.page_size || 20),
      });
      return response;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load activities");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPropertyActivities = useCallback(async (propertyId, filters = {}) => {
    return activityService.listPropertyActivities(propertyId, filters);
  }, []);

  const fetchTaskActivities = useCallback(async (taskId, filters = {}) => {
    return activityService.listTaskActivities(taskId, filters);
  }, []);

  const value = useMemo(
    () => ({
      activities,
      pagination,
      loading,
      error,
      fetchActivities,
      fetchPropertyActivities,
      fetchTaskActivities,
    }),
    [
      activities,
      pagination,
      loading,
      error,
      fetchActivities,
      fetchPropertyActivities,
      fetchTaskActivities,
    ],
  );

  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
}

ActivityProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useActivityContext = () => {
  const context = useContext(ActivityContext);

  if (!context) {
    throw new Error("useActivityContext must be used within ActivityProvider");
  }

  return context;
};

export default ActivityContext;
